import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Edit, AlertTriangle, User, Activity, Pill, FileText } from 'lucide-react';
import { Button, Badge, Card, CardHeader, CardTitle, CardContent, Label } from '@clinic-os/ui';
import { cn } from '@clinic-os/ui';
import { usePatient } from '../../hooks/usePatients';

type PatientTab = 'overview' | 'chart' | 'visits' | 'prescriptions';

const TABS: { id: PatientTab; label: string; icon: React.ElementType }[] = [
  { id: 'overview', label: 'Overview', icon: User },
  { id: 'chart', label: 'Chart', icon: Activity },
  { id: 'visits', label: 'Visits', icon: FileText },
  { id: 'prescriptions', label: 'Prescriptions', icon: Pill },
];

export function PatientDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data: patient, isLoading } = usePatient(id ?? '');
  const [activeTab, setActiveTab] = useState<PatientTab>('overview');

  if (isLoading) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-slate-200 rounded w-1/3" />
          <div className="h-4 bg-slate-200 rounded w-1/2" />
          <div className="h-32 bg-slate-200 rounded" />
        </div>
      </div>
    );
  }

  if (!patient) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <p className="text-slate-500">Patient not found.</p>
        <Link to="/patients" className="text-blue-600 hover:underline mt-2 inline-block">
          ← Back to patients
        </Link>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Link
          to="/patients"
          aria-label="Back to patients"
          className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
        >
          <ArrowLeft className="w-5 h-5 text-slate-600" />
        </Link>
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 text-lg font-semibold flex-shrink-0">
            {patient.firstName[0]}{patient.lastName[0]}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-semibold text-slate-900">
                {patient.firstName} {patient.lastName}
              </h1>
              <Button variant="outline" size="sm" className="gap-1.5">
                <Edit className="w-4 h-4" />
                Edit
              </Button>
            </div>
            <p className="text-sm text-slate-500 mt-0.5">
              DOB: {patient.dateOfBirth ?? 'Unknown'} · Patient since {new Date(patient.createdAt).getFullYear()}
            </p>
          </div>
        </div>
      </div>

      {/* Allergy warning bar */}
      {patient.allergies.length > 0 && (
        <div className="flex items-center gap-3 p-3 mb-6 rounded-lg bg-red-50 border border-red-200">
          <AlertTriangle className="w-5 h-5 text-red-700 flex-shrink-0" />
          <span className="text-sm font-medium text-red-800">Allergies: </span>
          <div className="flex flex-wrap gap-1.5">
            {patient.allergies.map((a) => (
              <Badge key={a} variant="error">{a}</Badge>
            ))}
          </div>
        </div>
      )}

      {/* Tab navigation */}
      <div className="mb-6 border-b border-slate-200" data-testid="chart-section">
        <nav className="flex gap-1 -mb-px" role="tablist">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                role="tab"
                aria-selected={isActive}
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  'flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors',
                  isActive
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                )}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab content */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Contact Info */}
          <Card className="lg:col-span-1">
            <CardHeader>
              <CardTitle>Contact Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <Label className="text-slate-500 text-xs uppercase tracking-wide">Email</Label>
                <p className="text-sm text-slate-700 mt-0.5">{patient.email ?? '—'}</p>
              </div>
              <div>
                <Label className="text-slate-500 text-xs uppercase tracking-wide">Phone</Label>
                <p className="text-sm text-slate-700 mt-0.5">{patient.phone ?? '—'}</p>
              </div>
              <div>
                <Label className="text-slate-500 text-xs uppercase tracking-wide">Notes</Label>
                <p className="text-sm text-slate-700 mt-0.5">{patient.notes ?? '—'}</p>
              </div>
            </CardContent>
          </Card>

          {/* Medical summary */}
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Current Medications</CardTitle>
              </CardHeader>
              <CardContent>
                {patient.medications.length > 0 ? (
                  <ul className="space-y-1">
                    {patient.medications.map((m) => (
                      <li key={m} className="text-sm text-slate-700 flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-blue-400 flex-shrink-0" />
                        {m}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-slate-500">No current medications recorded</p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Recent Invoices</CardTitle>
                <Link to="/invoices" className="text-sm text-blue-600 hover:underline">
                  View all
                </Link>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-500">Invoice history will appear here after billing is configured.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {activeTab === 'chart' && (
        <Card>
          <CardHeader>
            <CardTitle>Clinical Chart</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="py-12 text-center text-slate-500">
              <Activity className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p className="text-sm font-medium text-slate-500">No chart entries yet</p>
              <p className="text-xs text-slate-500 mt-1">Chart entries will appear here after appointments are documented via AI Notes.</p>
            </div>
          </CardContent>
        </Card>
      )}

      {activeTab === 'visits' && (
        <Card>
          <CardHeader>
            <CardTitle>Visit History</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="py-12 text-center text-slate-500">
              <FileText className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p className="text-sm font-medium text-slate-500">No visits recorded yet</p>
              <p className="text-xs text-slate-500 mt-1">Visit records will appear here after appointments are completed.</p>
            </div>
          </CardContent>
        </Card>
      )}

      {activeTab === 'prescriptions' && (
        <div className="space-y-6">
          <Card data-testid="prescription-form">
            <CardHeader>
              <CardTitle>New Prescription</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2 space-y-1.5">
                  <Label htmlFor="drug-name">Drug Name</Label>
                  <input
                    id="drug-name"
                    type="text"
                    placeholder="e.g. Amoxicillin 500mg"
                    className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="dosage">Dosage</Label>
                  <input
                    id="dosage"
                    type="text"
                    placeholder="e.g. 1 tablet"
                    className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="frequency">Frequency</Label>
                  <input
                    id="frequency"
                    type="text"
                    placeholder="e.g. 3x daily after meals"
                    className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="refills">Refills</Label>
                  <input
                    id="refills"
                    type="number"
                    min={0}
                    max={12}
                    defaultValue={0}
                    className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="duration">Duration (days)</Label>
                  <input
                    id="duration"
                    type="number"
                    min={1}
                    placeholder="e.g. 7"
                    className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div className="md:col-span-2 space-y-1.5">
                  <Label htmlFor="instructions">Special Instructions</Label>
                  <textarea
                    id="instructions"
                    rows={2}
                    placeholder="e.g. Take with food. Avoid alcohol."
                    className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  />
                </div>
              </div>
              <div className="flex gap-3 mt-4 pt-4 border-t border-slate-100">
                <Button>Save Prescription</Button>
                <Button variant="outline">Print Preview</Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Prescription History</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="py-8 text-center text-slate-500">
                <Pill className="w-8 h-8 mx-auto mb-2 opacity-40" />
                <p className="text-sm">No prescriptions issued yet</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
